import { MagpieConnection } from 'magpie-lib/connection';
import { defineMessage, defineTopic } from 'magpie-lib/models';
import Raven from 'raven-js';
import * as t from '../utils/schema';
import enviro from 'enviro';
import { Metrics } from '../utils/metrics';
import { devtoolsFlags } from '../features/devtools/flags';
/**
 * Magpie doesnt really make schema validation errors visible, so this helper
 * will log warnings if values being sent don't match the schema. This typically
 * happens when deep merging objects into the state.
 */
const prevalidatePublish = ({
  value,
  schema,
  context
}) => {
  const env = enviro.get('ENV');
  const isDeployed = enviro.deployed();
  if (isDeployed && env === 'production') {
    return;
  }
  const check = t.rawExecuteSchema({
    quick: false,
    schemaViolations: []
  }, schema, value);
  if (check.type === 'fail') {
    if (devtoolsFlags.leaderFollowerValidation) {
      console.warn('ReportNewLeaderValue validation error', check.schemaViolations, value, context);
    }
    Metrics.counter('magpie-publish-validation-error').increment();
  }
};

/**
 * Creates a Magpie-based synchronization strategy for leader-follower stores.
 *
 * This function sets up the necessary Magpie messages and topic for communication
 * between leader and follower stores, providing a reusable strategy that can be
 * used with different store implementations.
 *
 * @param key - A unique string key to identify this leader-follower pair
 * @param audience - The Magpie audience to use for messaging
 * @returns A SyncStrategy object with methods for store communication
 *
 * @example
 * ```typescript
 * import { CollaborationSidebarAudience } from '../utils/magpie';
 * import { createLeaderFollowerStore } from './leaderFollowerStore';
 *
 * const strategy = magpieStrategy<{ count: number }>({
 *   key: 'counter',
 *   audience: CollaborationSidebarAudience,
 *   valueSchema: t.object({ count: t.number })
 * });
 *
 * // Wire into leader/follower stores
 * const { makeLeader, makeFollower } = createLeaderFollowerStore<{
 *   count: number;
 * }>({
 *   syncStrategy: strategy,
 * });
 *
 * // Leader store instance
 * const leader = makeLeader({ count: 0 });
 *
 * // Follower store instance
 * const follower = makeFollower();
 *
 * // Subscribe to updates delivered via Magpie
 * const unsubscribeLeader = strategy.subscribeToLeaderUpdate((value) => {
 *   // follower -> leader updates
 *   console.log('Leader received', value);
 * });
 * const unsubscribeFollower = strategy.subscribeToFollowerUpdate((value) => {
 *   // leader -> follower updates
 *   console.log('Follower received', value);
 * });
 *
 * // Follower can request current state from leader
 * const unsubscribeRequest = strategy.subscribeToLeaderUpdateRequest(() => {
 *   strategy.updateFollower(leader.getState());
 * });
 * strategy.requestStateFromLeader();
 *
 * // Cleanup
 * unsubscribeLeader();
 * unsubscribeFollower();
 * unsubscribeRequest();
 * leader.cleanup();
 * follower.cleanup();
 * ```
 */

export const magpieStrategy = ({
  key,
  audience,
  valueSchema
}) => {
  const ReportNewLeaderValueMessage = defineMessage({
    type: `ReportNewLeaderValue`,
    payloadSchema: t.object({
      value: valueSchema,
      key: t.literal(key)
    })
  });
  const RequestNewLeaderValueMessage = defineMessage({
    type: `RequestNewLeaderValue`,
    reply: ReportNewLeaderValueMessage,
    payloadSchema: t.object({
      key: t.literal(key)
    })
  });
  const RequestUpdateLeaderValueMessage = defineMessage({
    type: `RequestUpdateLeaderValue`,
    payloadSchema: t.object({
      value: valueSchema,
      key: t.literal(key)
    })
  });
  const LeaderFollowerTopic = defineTopic({
    name: `LeaderFollowerTopic`,
    messages: [ReportNewLeaderValueMessage, RequestNewLeaderValueMessage, RequestUpdateLeaderValueMessage],
    audience,
    enableRuntimeValidation: false
  });
  const publishErrorConfig = {
    onValidationError: error => {
      Metrics.counter('magpie-publish-validation-error').increment();
      Raven.captureException(error);
    },
    onError: error => {
      Metrics.counter('magpie-publish-error').increment();
      Raven.captureException(error);
    }
  };
  const subscriptionConfig = {
    onError: error => {
      Metrics.counter('magpie-subscription-error').increment();
      Raven.captureException(error);
    }
  };
  return {
    setupLeader: ({
      onNewLeaderValueRequest,
      onUpdateLeaderValueRequest
    }) => {
      const topic = MagpieConnection.get().forTopic(LeaderFollowerTopic);
      const unsubscribeRequestNewLeaderValue = topic.on(RequestNewLeaderValueMessage.getType(), (_, api) => {
        const newLeaderValue = onNewLeaderValueRequest();
        api.reply(ReportNewLeaderValueMessage.create({
          value: newLeaderValue,
          key
        }));
      }, Object.assign({}, subscriptionConfig, {
        filter: message => message.payload.key === key
      }));
      const unsubscribeRequestUpdateLeaderValue = topic.on(RequestUpdateLeaderValueMessage.getType(), message => {
        onUpdateLeaderValueRequest(message.payload.value);
      }, Object.assign({}, subscriptionConfig, {
        filter: message => message.payload.key === key
      }));
      return {
        reportNewLeaderValue: newValue => {
          prevalidatePublish({
            value: newValue,
            schema: valueSchema,
            context: 'reportNewLeaderValue'
          });
          topic.publish(ReportNewLeaderValueMessage.create({
            value: newValue,
            key
          }), publishErrorConfig);
        },
        cleanup: () => {
          unsubscribeRequestNewLeaderValue();
          unsubscribeRequestUpdateLeaderValue();
        }
      };
    },
    setupFollower: ({
      onNewLeaderValue
    }) => {
      const topic = MagpieConnection.get().forTopic(LeaderFollowerTopic);
      const unsubscribeNewLeaderValue = topic.on(ReportNewLeaderValueMessage.getType(), message => {
        onNewLeaderValue(message.payload.value);
      }, Object.assign({}, subscriptionConfig, {
        filter: message => message.payload.key === key
      }));
      topic.publish(RequestNewLeaderValueMessage.create({
        key
      }), Object.assign({}, publishErrorConfig, {
        onReply: reply => {
          onNewLeaderValue(reply.payload.value);
        }
      }));
      return {
        requestUpdateLeaderValue: newValue => {
          prevalidatePublish({
            value: newValue,
            schema: valueSchema,
            context: 'requestUpdateLeaderValue'
          });
          topic.publish(RequestUpdateLeaderValueMessage.create({
            value: newValue,
            key
          }), publishErrorConfig);
        },
        cleanup: () => {
          unsubscribeNewLeaderValue();
        }
      };
    }
  };
};